import { useState } from 'react';

import Head from 'next/head';
import Image from 'next/image';

import { Box, Container, Grid, Hidden, Typography, Button, MobileStepper } from '@mui/material';

import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import SwipeableViews from 'react-swipeable-views';
import { autoPlay } from 'react-swipeable-views-utils';

import useSWR from 'swr';

import Stats from '../components/Stats';

import TopNav from '../components/TopNav';
import BottomNav from '../components/BottomNav';

import { NextLinkComposed } from '../components/Link';

import gaEvent from '../lib/gaEvent';


const AutoPlaySwipeableViews = autoPlay(SwipeableViews);


export default function ForMechanics() {

  const testimonialSteps = [
    {
      quote: 'We tried using different platforms. MechanicBuddy delivers pre-qualified customers who need our services. The biggest benefit is that we quote and respond directly to clients in real-time, which speeds up our sales cycle. We are winning more jobs with MechanicBuddy.',
      bgImg: '/img/testimonial-images/jump-mobile.jpg',
      name: 'Colin Shepherd',
      business: 'Jump Mobile Mechanics',
    },
    {
      quote: 'Apart from the clients we have acquired through the website, we have also had new clients who either saw our profile on MechanicBuddy or were referred to us by clients who have used MechanicBuddy',
      bgImg: '/img/testimonial-images/performax.jpg',
      name: 'Peter Hardy',
      business: 'Performax Tuning',
    },
    {
      quote: 'The website is user friendly and it is easy and convenient to send quotes to potential clients generated through the site.',
      bgImg: '/img/testimonial-images/rev-n-roar.jpg',
      name: 'Jaco & Germayne Slabbert',
      business: 'Rev N Roar Motor Services',
    },
  ];
  const maxSteps = testimonialSteps.length;
  const [activeStep, setActiveStep] = useState(0);

  const { data: counters } = useSWR('/content/counters/');

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };
  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };
  const handleStepChange = step => {
    setActiveStep(step);
  };

  return <>
    <Head>
      <title>The Best Way for Mechanics & Workshops to Meet New Customers</title>
      <meta
        name="description"
        content="Increase your online presence and connect with new customers. Build a good reputation with good reviews."
      />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.mechanicbuddy.co.za/for-mechanics/" />
      <meta property="og:title" content="We deliver local customers to your inbox" />
      <meta
        property="og:description"
        content="MechanicBuddy is the #1 car repair website that connects car owners with mechanics across South Africa."
      />
      <meta
        property="og:image"
        content="https://www.mechanicbuddy.co.za/logos/mechanicbuddy-text-logo-square.jpg"
      />

      {/* Twitter */}
      <meta property="twitter:card" content="summary" />
      <meta property="twitter:url" content="https://www.mechanicbuddy.co.za/for-mechanics/" />
      <meta property="twitter:title" content="We deliver local customers to your inbox" />
      <meta
        property="twitter:description"
        content="MechanicBuddy is the #1 car repair website that connects car owners with mechanics across South Africa."
      />
      <meta
        property="twitter:image"
        content="https://www.mechanicbuddy.co.za/logos/mechanicbuddy-text-logo-square.jpg"
      />

      <link rel="canonical" href="https://www.mechanicbuddy.co.za/for-mechanics/" />
    </Head>

    <TopNav />

    <Box
      sx={{
        bgcolor: 'primary.main',
        py: '120px',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundImage: 'url("/img/happy-customer.jpg")',
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            color: "white",
            alignItems: {
              xs: 'center',
              md: 'flex-start',
            },
            textAlign: {
              xs: 'center',
              md: 'left',
            }
          }}
        >
          <Typography variant="h2" component="h1" gutterBottom>
            Meet new Customers
          </Typography>
          <Typography variant="h6" component="h2">
            MechanicBuddy is the #1 car repair website that connects
            <Hidden mdDown>
              <br />
            </Hidden>{' '}
            car owners with mechanics across South Africa.
          </Typography>

          <Box mt={3}>
            <Button
              color="secondary"
              variant="contained"
              onClick={() => {
                gaEvent({
                  category: 'Register Business',
                  action: 'Click Register',
                  label: 'Register Now',
                });
              }}
              size="large"
              component={NextLinkComposed}
              to={{ pathname: '/register-business/' }}
            >
              Register Now
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>

    <Box width="100%" bgcolor="white">
      <Container
        maxWidth="md"
        sx={{
          p: theme => theme.spacing(8, 0),
        }}
      >
        <Box textAlign="center" mb={6} px={2}>
          <Typography variant="h3">We deliver local customers to your inbox</Typography>
        </Box>

        <Grid container justifyContent="center">
          <Grid item md={4}>
            <Box textAlign="center" p={2}>
              <Image
                src="/icons/customer-illustration.svg"
                width={200}
                height={200}
                alt="Customers"
                title="Gain new customers"
              />
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Gain new{' '}
                <Hidden mdDown>
                  <br />
                </Hidden>
                lifetime customers
              </Typography>
            </Box>
          </Grid>
          <Grid item md={4}>
            <Box textAlign="center" p={2}>
              <Image
                src="/icons/reviews-thumbnail.svg"
                width={200}
                height={200}
                alt="Reviews"
                title="Build a good reputation with good reviews"
              />
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Build a good reputation{' '}
                <Hidden mdDown>
                  <br />
                </Hidden>
                with good reviews
              </Typography>
            </Box>
          </Grid>
          <Grid item md={4}>
            <Box textAlign="center" p={2}>
              <Image
                src="/icons/search-illustration.svg"
                width={200}
                height={200}
                alt="Google search"
                title="Be visible in google search results"
              />
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Be visible in{' '}
                <Hidden mdDown>
                  <br />
                </Hidden>
                Google search results
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>

    <Box width="100%" bgcolor="primary.dark" color="white">
      <Container
        maxWidth="md"
        sx={{
          p: theme => theme.spacing(8, 0),
        }}
      >
        <Box textAlign="center" mb={6}>
          <Typography variant="h3">How It Works</Typography>
        </Box>

        <Grid container justifyContent="space-between">
          <Grid item xs={12} md={3}>
            <Box textAlign="center" p={2}>
              <Image
                src="/icons/post-requests.svg"
                width={120}
                height={120}
                alt="Potential clients"
                title="Potential clients"
              />
              <Typography sx={{ mt: 2, mb: 1 }}>
                Potential clients post car repair and service jobs
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box textAlign="center" p={2}>
              <Image
                src="/icons/get-notified.svg"
                width={120}
                height={120}
                alt="Get notified"
                title="Get notified"
              />
              <Typography sx={{ mt: 2, mb: 1 }}>
                We notify you of the opportunities
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box textAlign="center" p={2}>
              <Image
                src="/icons/provide-quotes.svg"
                width={120}
                height={120}
                alt="Provide quotes"
                title="Provide quotes"
              />
              <Typography sx={{ mt: 2, mb: 1 }}>
                You provide quotes and make deals
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box textAlign="center" p={2}>
              <Image
                src="/icons/accepted.svg"
                width={120}
                height={120}
                alt="Quote accepted"
                title="Quote accepted"
              />
              <Typography sx={{ mt: 2, mb: 1 }}>Build your client base</Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>

    <Grid item xs={12}>
      <Box position='relative'>
      <AutoPlaySwipeableViews
        axis="x"
        index={activeStep}
        onChangeIndex={handleStepChange}
        enableMouseEvents
        interval={15000}
      >
        {testimonialSteps.map(step => (
          <div key={step.name}>
            <Hidden mdUp>
              <Box p={4}>
                <Image 
                  src="/icons/quotes.png"
                  width={100}
                  height={78}
                  alt="Mechanic"
                />
                <Box
                  fontSize="16px"
                  fontWeight={300}
                  fontStyle="italic"
                  color="primary.main"
                  my={2}
                >
                  {step.quote}
                </Box>
                <Box fontSize="14px" color="primary.main">
                  <Box component="span" color="secondary.main" fontWeight="700">
                    {step.name}
                  </Box>{' '}
                  | {step.business}
                </Box>
              </Box>
            </Hidden>
            <Hidden mdDown>
              <Box
                key={step.id}
                style={{ backgroundImage: `url(${step.bgImg})` }}
                sx={{
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  pt: '60px',
                  height: {
                    md: '512px'
                  }
                }}
              >
                <Container
                  maxWidth="lg"
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Grid item xs={12} md={7}>
                    <Image 
                      src="/icons/quotes.png"
                      alt="Mechanic"
                      width={100}
                      height={78}
                    />
                    <Box fontSize="24px" fontWeight={300} fontStyle="italic" color="white" my={3}>
                      {step.quote}
                    </Box>
                    <Box fontSize="25px" color="white">
                      <Box component="span" color="secondary.main" fontWeight="700">
                        {step.name}
                      </Box>{' '}
                      | {step.business}
                    </Box>
                  </Grid>
                </Container>
              </Box>
            </Hidden>
          </div>
        ))}
      </AutoPlaySwipeableViews>
      <MobileStepper
        variant="dots"
        steps={maxSteps}
        position="static"
        activeStep={activeStep}
        sx={{
          flexGrow: 1,
          '& .MuiButton-root': {
            position: 'relative',
            color: {
              xs: '',
              md: 'white',
            },
            minWidth: '0',
          },
          '& .MuiMobileStepper-dot': {
            margin: '0 15px',
            width: '13px',
            height: '13px',
            bgcolor: {
              xs: '#153545',
              md: 'white',
            },
            position: 'relative',
          },
          '& .MuiMobileStepper-dotActive': {
            '&::before': {
              content: `''`,
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              left: '-5px',
              display: 'block',
              width: '23px',
              height: '23px',
              border: '1px solid #153545',
              borderRadius: '23px',
            },
          },
          '& .Mui-disabled.Mui-disabled': {
            opacity: '20%',
          },
          position: {
            md: 'absolute'
          },
          left: {
            md: '41%'
          },
          bottom: {
            md: '45px'
          },
          backgroundColor: {
            md: 'transparent'
          },
          // [theme.breakpoints.up('md')]: {
          //   '& .MuiMobileStepper-dotActive': {
          //     '&::before': {
          //       border: '1px solid white !important',
          //     },
          //   },
          // },
        }}
        nextButton={
          <Button
            size="small"
            onClick={handleNext}
            disabled={activeStep === maxSteps - 1}
            aria-label="Next Review"
          >
            <KeyboardArrowRight />
          </Button>
        }
        backButton={
          <Button
            size="small"
            onClick={handleBack}
            disabled={activeStep === 0}
            aria-label="Previous Review"
          >
            <KeyboardArrowLeft />
          </Button>
        }
      />
      </Box>
    </Grid>

    <Box bgcolor="white" py={8}>
      <Container maxWidth="md">
        <Grid container justifyContent="space-between">
          <Stats
            value={counters ? counters.job_count : '--'}
            title={<>JOBS REQUESTED</>}
          />
          <Stats value={counters ? counters.estimate_count : '--'} title={<>ESTIMATES GIVEN</>} />
          <Stats value={counters ? counters.business_count : '--'} title={<>MECHANICS LISTED</>} />
        </Grid>
      </Container>
    </Box>
    <BottomNav />
  </>;
}
